import taskmanAxios from '../helpers/axios'

import {
    API_MAKE_TIME_LOG_URL,
    API_MAKE_TASK_TIME_LOGS_URL,
    API_MY_TIME_LOGS_URL,
    API_TIME_LOGS_URL,
} from "../constants/api.constant";

function extractTimeLogs(data: any) {
    if (Array.isArray(data)) {
        return data
    }

    if (Array.isArray(data?.items)) {
        return data.items
    }

    if (Array.isArray(data?.results)) {
        return data.results
    }

    if (Array.isArray(data?.time_logs)) {
        return data.time_logs
    }

    if (Array.isArray(data?.data)) {
        return data.data
    }

    return []
}

function extractTimeLog(data: any) {
    if (!data || Array.isArray(data) || typeof data !== 'object') {
        return extractTimeLogs(data)[0] || null
    }

    if (data.id != null || data.minutes_spent != null) {
        return data
    }

    for (const key of ['data', 'item', 'time_log', 'timeLog']) {
        const nestedTimeLog = extractTimeLog(data[key])

        if (nestedTimeLog) {
            return nestedTimeLog
        }
    }

    return extractTimeLogs(data)[0] || null
}

export default class TimeLogService {
    static async list(params: Record<string, any> = {}) {
        return await taskmanAxios.get(API_TIME_LOGS_URL, { params })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async listMine(params: Record<string, any> = {}) {
        return await taskmanAxios.get(API_MY_TIME_LOGS_URL, { params })
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async get(timeLogId: number | string) {
        return await taskmanAxios.get(API_MAKE_TIME_LOG_URL(timeLogId))
            .then(response => extractTimeLog(response.data))
            .catch(e => Promise.reject(e));
    }

    static async getByTaskId(taskId: number | string) {
        return await taskmanAxios.get(API_MAKE_TASK_TIME_LOGS_URL(taskId))
            .then(response => extractTimeLog(response.data))
            .catch(e => Promise.reject(e));
    }

    static async create(data: { task_id: number | string, minutes_spent: number, description?: string }) {
        return await taskmanAxios.post(API_TIME_LOGS_URL, data)
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async update(timeLogId: number | string, data: { task_id: number | string, minutes_spent: number, description?: string }) {
        return await taskmanAxios.put(API_MAKE_TIME_LOG_URL(timeLogId), data)
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async delete(timeLogId: number | string) {
        return await taskmanAxios.delete(API_MAKE_TIME_LOG_URL(timeLogId))
            .then(response => response.data)
            .catch(e => Promise.reject(e));
    }

    static async syncForTask({
        taskId,
        spendTime,
        description = '',
        timeLogId = null,
    }: {
        taskId: number | string
        spendTime?: number | null
        description?: string
        timeLogId?: number | string | null
    }) {
        const normalizedSpendTime = Number(spendTime)
        const hasTime = spendTime != null && !Number.isNaN(normalizedSpendTime) && normalizedSpendTime > 0
        const minutesSpent = hasTime ? Math.round(normalizedSpendTime * 60) : 0

        let existingTimeLog = null

        if (timeLogId != null) {
            existingTimeLog = await this.get(timeLogId)
        } else {
            try {
                existingTimeLog = await this.getByTaskId(taskId)
            } catch (error: any) {
                const status = error?.status ?? error?.response?.status

                // The task endpoint returns 404 when the first time log has
                // not been created yet. That is an empty state, not a failed
                // save: continue below and create the log.
                if (status !== 404) {
                    throw error
                }
            }
        }

        const currentTimeLogId = existingTimeLog?.id ?? timeLogId ?? null

        if (!minutesSpent) {
            if (currentTimeLogId != null) {
                await this.delete(currentTimeLogId)
            }

            return null
        }

        const payload = {
            task_id: taskId,
            minutes_spent: minutesSpent,
            description,
        }

        const existingMinutesSpent = Number(existingTimeLog?.minutes_spent)
        const existingDescription = existingTimeLog?.description == null
            ? ''
            : String(existingTimeLog.description)

        if (
            existingTimeLog
            && Number.isFinite(existingMinutesSpent)
            && existingMinutesSpent === minutesSpent
            && existingDescription === description
        ) {
            return existingTimeLog
        }

        if (currentTimeLogId != null) {
            return await this.update(currentTimeLogId, payload)
        }

        return await this.create(payload)
    }
}
